import { CountryCode, parseNumber } from 'libphonenumber-js';
import get from 'lodash/get';
import toUpper from 'lodash/toUpper';

import { COUNTRY_CODES } from '@savgroup-front-common/constants';

import { isPhoneNumber } from './validators';

/**
 * Validates if a country code is supported by the application
 * @param countryCode - The country code to validate
 * @returns True if the country code is supported
 */
const isValidSupportedCountryCode = (countryCode: string): boolean => {
  if (!countryCode) {
    return false;
  }

  const upperCountryCode = toUpper(countryCode);

  return Object.values(COUNTRY_CODES).includes(
    upperCountryCode as COUNTRY_CODES,
  );
};

/**
 * Validates a phone number with proper country code handling
 * @param phoneNumber - The phone number to validate
 * @param countryCode - The country code (optional)
 * @returns True if the phone number is valid
 */
export const isValidPhoneNumber = (
  phoneNumber: string | undefined,
  countryCode?: string,
) => {
  if (!phoneNumber) {
    return false;
  }

  // Normalize phone number: replace 00 with + for international format
  const normalizedPhone = phoneNumber.replace(/^00/, '+');

  // If no country code provided, try to detect French numbers and use FR as default
  if (!countryCode) {
    // Check if it looks like a French national number (starts with 0 and has 10 digits)
    if (/^0\d{9}$/.test(phoneNumber)) {
      const parsedWithFR = parseNumber(normalizedPhone, 'FR' as CountryCode);

      return isPhoneNumber(phoneNumber) && Boolean(get(parsedWithFR, 'phone'));
    }

    const parsedWithoutCountry = parseNumber(normalizedPhone);

    return (
      isPhoneNumber(phoneNumber) && Boolean(get(parsedWithoutCountry, 'phone'))
    );
  }

  const upperCountryCode = toUpper(countryCode);

  // If country code is not supported by our app, try to detect French numbers or parse without country code
  if (!isValidSupportedCountryCode(upperCountryCode)) {
    // Check if it looks like a French national number (starts with 0 and has 10 digits)
    if (/^0\d{9}$/.test(phoneNumber)) {
      const parsedWithFR = parseNumber(normalizedPhone, 'FR' as CountryCode);

      return isPhoneNumber(phoneNumber) && Boolean(get(parsedWithFR, 'phone'));
    }

    const parsedWithoutCountry = parseNumber(normalizedPhone);

    return (
      isPhoneNumber(phoneNumber) && Boolean(get(parsedWithoutCountry, 'phone'))
    );
  }

  // If country code is supported, parse with it
  const parsedPhoneNumber = parseNumber(
    normalizedPhone,
    upperCountryCode as CountryCode,
  );

  return isPhoneNumber(phoneNumber) && Boolean(get(parsedPhoneNumber, 'phone'));
};
